import classNames from 'classnames'
import { useField } from 'formik'
import { useRef } from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { IconAlert } from '@/components/Icons'

type Props = {
  name: string
  label?: string
  labelHelper?: string
  placeholder?: string
  rows?: number
  maxRows?: number
  resizable?: boolean
  autosize?: boolean
  className?: string
  maxLength?: number
  disabled?: boolean
  autoFocus?: boolean
  placeholderColor?: 'light' | 'mid' | 'dark'
  withoutOutline?: boolean
  bordered?: boolean
}

const Textarea: React.FC<React.PropsWithChildren<Props>> = ({
  label,
  labelHelper,
  resizable = true,
  autosize = false,
  className = 'p-2.5 rounded',
  placeholderColor = 'light',
  withoutOutline = false,
  bordered,
  ...props
}) => {
  const [field, { touched, error }] = useField(props)
  const ref = useRef<HTMLTextAreaElement>(null)
  const hasError = touched && error && typeof error === 'string'

  const classes = classNames(
    'w-full py-3 px-4 rounded-md outline-none',
    {
      'border border-grey-light focus:border-purple-base': bordered,
      'focus:ring focus:ring-purple-extra-light': !withoutOutline,
      'placeholder:text-grey-base': placeholderColor === 'light',
      'placeholder:text-grey-dark': placeholderColor === 'mid',
      'placeholder:text-navy-dark': placeholderColor === 'dark',
      'border border-red-base': hasError,
      'resize-none': !resizable,
      'overflow-y-hidden': autosize,
    },
    className,
  )

  return (
    <div className="relative text-left">
      <label htmlFor={props.name} className="relative block">
        {label ? (
          <div className="mb-2 whitespace-pre-line font-medium">
            {label} {labelHelper ? <span className="font-normal text-grey-base">{labelHelper}</span> : null}
          </div>
        ) : null}

        {autosize ? (
          <TextareaAutosize
            ref={ref}
            minRows={props.rows}
            maxRows={props.maxRows}
            id={props.name}
            className={classes}
            {...field}
            {...props}
          />
        ) : (
          <textarea ref={ref} id={props.name} className={classes} {...field} {...props} />
        )}

        {props.maxLength ? (
          <div className="mt-2 text-right text-grey-base text-xs">
            {ref?.current?.value?.length ?? 0}/{props.maxLength}
          </div>
        ) : null}
      </label>

      {hasError ? (
        <div className="mt-3 flex items-center text-red-base text-sm">
          <IconAlert className="fill-current icon-xs -mt-0.5 mr-1 inline-block text-red-base" />
          {error}
        </div>
      ) : null}
    </div>
  )
}

export default Textarea
